import { Controller } from "@hotwired/stimulus"
import $ from "jquery"

export default class extends Controller {
  static targets = ["street", "city", "postalCode", "country", "manualButton", "streetWrapper", "additionalFields"]
  static values = { manual: Boolean }

  connect() {
    this.manualValue = false

    // Check if fields already have values (editing existing practice)
    if (this.hasExistingValues()) {
      this.showAdditionalFields()
      this.showStreetField()
    } else {
      // Default: hide additional fields
      this.hideAdditionalFields()
    }

    // Wait for Google Maps API to load
    if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
      console.error('Google Maps Places API not loaded')
      return
    }

    this.initializeAutocomplete()
  }

  hasExistingValues() {
    return (this.hasCityTarget && this.cityTarget.value.trim() !== '') ||
           (this.hasPostalCodeTarget && this.postalCodeTarget.value.trim() !== '') ||
           (this.hasCountryTarget && this.countryTarget.value.trim() !== '')
  }

  initializeAutocomplete() {
    this.autocomplete = new google.maps.places.Autocomplete(this.streetTarget, {
      types: ['address'],
      fields: ['address_components', 'formatted_address']
    })

    this.autocomplete.addListener('place_changed', () => {
      this.fillInAddress()
    })
  }

  fillInAddress() {
    const place = this.autocomplete.getPlace()

    if (!place.address_components) {
      return
    }

    // Show additional fields when address is selected
    this.showAdditionalFields()

    // Clear all fields first
    this.streetTarget.value = ''
    if (this.hasCityTarget) this.cityTarget.value = ''
    if (this.hasPostalCodeTarget) this.postalCodeTarget.value = ''
    if (this.hasCountryTarget) this.countryTarget.value = ''

    // Extract address components
    let streetNumber = ''
    let route = ''

    for (const component of place.address_components) {
      const componentType = component.types[0]

      switch (componentType) {
        case 'street_number':
          streetNumber = component.long_name
          break
        case 'route':
          route = component.long_name
          break
        case 'locality':
          if (this.hasCityTarget) {
            this.cityTarget.value = component.long_name
          }
          break
        case 'postal_code':
          if (this.hasPostalCodeTarget) {
            this.postalCodeTarget.value = component.long_name
          }
          break
        case 'country':
          if (this.hasCountryTarget) {
            this.countryTarget.value = component.long_name
          }
          break
      }
    }

    // Combine street number and route for street address
    this.streetTarget.value = `${streetNumber} ${route}`.trim()

    // Re-validate fields with Parsley to clear any validation errors
    this.revalidateFields()
  }

  revalidateFields() {
    const fieldsToValidate = [this.streetTarget]
    if (this.hasCityTarget) fieldsToValidate.push(this.cityTarget)
    if (this.hasPostalCodeTarget) fieldsToValidate.push(this.postalCodeTarget)
    if (this.hasCountryTarget) fieldsToValidate.push(this.countryTarget)

    fieldsToValidate.forEach(field => {
      const $field = $(field)

      // Remove error classes
      $field.removeClass('border-red-500 parsley-error')

      // Remove error messages from field wrapper
      const $wrapper = $field.closest('.field-wrapper')
      $wrapper.find('.parsley-errors-list').remove()

      // Re-validate with Parsley if available
      if ($field.parsley && $field.parsley()) {
        $field.parsley().validate()
      }
    })
  }

  toggleMode(event) {
    event.preventDefault()

    if (this.manualValue) {
      this.enableGoogleMode()
    } else {
      this.enableManualMode()
    }
  }

  enableManualMode() {
    this.manualValue = true

    // Disable Google autocomplete
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete)
      this.autocomplete = null
    }

    // Remove pac-container (Google dropdown)
    const pacContainers = document.querySelectorAll('.pac-container')
    pacContainers.forEach(container => container.remove())

    // Clear all fields
    this.clearFields()

    // Hide street field, show additional fields
    this.hideStreetField()
    this.showAdditionalFields()

    // Update button text
    if (this.hasManualButtonTarget) {
      this.manualButtonTarget.textContent = 'Use Google autocomplete'
    }

    // Setup real-time field sync for manual mode
    this.setupManualModeSync()
  }

  setupManualModeSync() {
    // Add input listeners to update street field in real-time
    const fieldsToWatch = ['city', 'postalCode', 'country']

    fieldsToWatch.forEach(field => {
      const targetName = `has${field.charAt(0).toUpperCase() + field.slice(1)}Target`
      const target = `${field}Target`

      if (this[targetName]) {
        // Remove existing listener if any
        if (this[`${field}InputHandler`]) {
          this[target].removeEventListener('input', this[`${field}InputHandler`])
        }

        // Add new listener
        this[`${field}InputHandler`] = () => this.updateStreetFromManualFields()
        this[target].addEventListener('input', this[`${field}InputHandler`])
      }
    })
  }

  updateStreetFromManualFields() {
    if (!this.manualValue) return

    const city = this.hasCityTarget ? this.cityTarget.value.trim() : ''
    const postalCode = this.hasPostalCodeTarget ? this.postalCodeTarget.value.trim() : ''
    const country = this.hasCountryTarget ? this.countryTarget.value.trim() : ''

    // Combine into street address: "City, Postal Code, Country"
    const parts = [city, postalCode, country].filter(part => part !== '')
    this.streetTarget.value = parts.join(', ')
  }

  removeManualModeSync() {
    const fieldsToWatch = ['city', 'postalCode', 'country']

    fieldsToWatch.forEach(field => {
      const targetName = `has${field.charAt(0).toUpperCase() + field.slice(1)}Target`
      const target = `${field}Target`

      if (this[targetName] && this[`${field}InputHandler`]) {
        this[target].removeEventListener('input', this[`${field}InputHandler`])
        this[`${field}InputHandler`] = null
      }
    })
  }

  enableGoogleMode() {
    this.manualValue = false

    // Remove manual mode sync listeners
    this.removeManualModeSync()

    // Clear all fields
    this.clearFields()

    // Show street field, hide additional fields
    this.showStreetField()
    this.hideAdditionalFields()

    // Re-initialize Google autocomplete
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      this.initializeAutocomplete()
    }

    // Update button text
    if (this.hasManualButtonTarget) {
      this.manualButtonTarget.textContent = 'Enter address manually'
    }

    // Update street field placeholder
    this.streetTarget.placeholder = 'Start typing your address...'
  }

  clearFields() {
    this.streetTarget.value = ''
    if (this.hasCityTarget) this.cityTarget.value = ''
    if (this.hasPostalCodeTarget) this.postalCodeTarget.value = ''
    if (this.hasCountryTarget) this.countryTarget.value = ''
  }

  hideAdditionalFields() {
    if (this.hasAdditionalFieldsTarget) {
      this.additionalFieldsTarget.classList.add('hidden')
    }
  }

  showAdditionalFields() {
    if (this.hasAdditionalFieldsTarget) {
      this.additionalFieldsTarget.classList.remove('hidden')
    }
  }

  hideStreetField() {
    if (this.hasStreetWrapperTarget) {
      this.streetWrapperTarget.classList.add('hidden')
    }
  }

  showStreetField() {
    if (this.hasStreetWrapperTarget) {
      this.streetWrapperTarget.classList.remove('hidden')
    }
  }

  disconnect() {
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete)
    }
  }
}
